import {
  Cell,
  ChatAssistantMessage,
  ChatExploration,
  ChatMessage,
  ChatUserMessage,
  ConversationCell,
} from '@/explore/types';

import { isChatCell } from './chat-cell';

export const isConversationCell = (cell: Cell): cell is ConversationCell =>
  'conversationId' in cell;

export function getConversationCells(cells: Cell[], conversationId: string): ConversationCell[] {
  // Double filtering is required here to satisfy types
  return cells.filter(isConversationCell).filter((cell) => cell.conversationId === conversationId);
}

export function convertConversationCellsToMessages(cells: ConversationCell[]) {
  const result: (ChatMessage | ChatExploration)[] = [];
  let nonChatCells: ConversationCell[] = [];

  cells.forEach((cell) => {
    if (!isChatCell(cell)) {
      return nonChatCells.push(cell);
    }

    // If there are non-chat cells collected, wrap them in an Exploration object
    if (nonChatCells.length > 0) {
      result.push({
        id: cell.id,
        role: 'assistant',
        type: 'exploration',
        exploration: {
          explorationId: cell.conversationId,
          name: 'Exploration',
          labels: {},
          parameters: [],
          view: { cells: nonChatCells },
        },
      } as ChatExploration);
      nonChatCells = [];
    }

    cell.messages.forEach((message) => {
      if (message.role === 'assistant') {
        const chatMessage: ChatAssistantMessage = {
          id: message.id,
          role: 'assistant',
          type: 'clarifying_question',
          message: message.message || '',
        };
        result.push(chatMessage);
      }

      if (message.role === 'user') {
        const chatMessage: ChatUserMessage = {
          id: message.id,
          role: 'user',
          type: message.type as 'initial_user_prompt' | 'clarifying_answer' | 'followup_question',
          message: message.message || '',
        };
        result.push(chatMessage);
      }
    });
  });

  // If there are remaining non-chat cells, wrap them in an Exploration object
  if (nonChatCells.length > 0) {
    result.push({
      id: nonChatCells[0].id,
      role: 'assistant',
      type: 'exploration',
      exploration: {
        explorationId: nonChatCells[0].conversationId,
        name: 'Exploration',
        labels: {},
        parameters: [],
        view: { cells: nonChatCells },
      },
    });
  }

  return result;
}

export function unlinkConversationCell(cell: ConversationCell): Cell {
  if (isChatCell(cell)) {
    return cell;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { conversationId: _, ...rest } = cell;
  return rest;
}
